import { CustomTooltip } from "@/components/CustomTooltip";
import theme from "@/styles/theme";
import {
	BusinessRounded,
	AssignmentIndRounded,
	BadgeRounded,
	SettingsRounded,
	ChatRounded,
	SouthRounded,
	TurnSharpLeftRounded,
	TurnSharpRightRounded,
	ArrowForwardRounded,
	NorthRounded,
	WestRounded,
} from "@mui/icons-material";
import { Alert, Box, Button, Paper, Stack, Typography } from "@mui/material";
import { blueGrey, orange } from "@mui/material/colors";

type StepProps = {
	label: string;
	nodeType: "initial" | "step" | "final" | "decision";
	actorType?: "organization" | "ethicist" | "member" | "system";
	content?: {
		text: string;
		items?: string[];
	}[];
};

const STEPS: StepProps[] = [
	{
		label: "Cadastro aplicação",
		nodeType: "initial",
		actorType: "organization",
		content: [
			{
				text: "Configurações",
				items: ["Nome", "Descrição", "Área", "Público"],
			},
			{
				text: "Perguntas de enquadramento",
			},
		],
	},
	{
		label: "Atribuições de Questionários AIAs e EPS",
		nodeType: "step",
		actorType: "system",
		content: [
			{
				text: "Questionários AIAs são atribuídos à aplicação conforme respostas 'Sim' das perguntas de enquadramento",
			},
			{
				text: "Questionário EPS é vinculado sempre",
			},
			{
				text: "Os questionários vinculados são as versões publicadas no momento de cadastro da aplicação",
			},
		],
	},
	{
		label: "Resolução de questionários",
		nodeType: "step",
		actorType: "organization",
		content: [
			{
				text: "Questionários AIAs",
				items: [
					"Após cada submissão, resultados dos impactos éticos são imediatamente disponibilizados",
					"Somente após todos respondidos, o questionário EPS estará disponibilizado para resolução",
				],
			},
			{
				text: "Questionário EPS",
				items: [
					"Aborda temas subjetivos e de redação livre",
					"Após a sua conclusão é possível submeter a aplicação para avaliação de um eticista",
				],
			},
		],
	},
	{
		label: "Submissão de aplicação para avaliação",
		nodeType: "step",
		actorType: "organization",
		content: [
			{
				text: "A aplicação fica disponível para eticistas avaliarem",
			},
			{
				text: "O responsável pela organização pode cancelar a submissão enquanto NÃO houver eticista atribuído como avaliador",
			},
			{
				text: "O andamento da avaliação da aplicação pode ser acompanhada visualizando as datas no histórico",
			},
		],
	},
	{
		label: "Avaliação e emissão de parecer",
		nodeType: "step",
		actorType: "ethicist",
		content: [
			{
				text: "O eticista analisa o contexto da aplicação e respostas prévias dos questionários submetidos anteriormente",
			},
			{
				text: "Atribuição de níveis de impacto para cada princípio ético envolvido na aplicação, assim como considerações caso aplicável",
			},
			{
				text: "Redação do parecer final com as orientações, sugestões éticas e legais à serem tratadas na aplicação",
			},
		],
	},
	{
		label: "Deliberação de parecer",
		nodeType: "step",
		actorType: "member",
		content: [
			{
				text: "Avaliação do parecer emitido para fins de considerações de linhas de pesquisa, opinião emitida ao contexto da aplicação e validação técnica, deliberando ao final do processo",
			},
		],
	},
	{
		label: "Revisão",
		nodeType: "decision",
	},
	{
		label: "Aprovação",
		nodeType: "decision",
	},
	{
		label: "Disponibilização de resultados",
		nodeType: "final",
		actorType: "system",
		content: [
			{
				text: "Exibição do parecer formal do eticista",
			},
			{
				text: "Interpretação dos resultados dos graus de impacto dos princípios, assim como as considerações, caso aplicável",
			},
			{
				text: "Visualização das orientações conforme cada nível de impacto atribuído",
			},
		],
	},
];

function nodeContent({
	nodeType,
	actorType,
}: {
	nodeType: StepProps["nodeType"];
	actorType?: StepProps["actorType"];
}) {
	const nodeStyles = {
		initial: {
			borderLeftColor: blueGrey[900],
			width: 120,
		},
		step: {
			borderLeftColor: orange[600],
			width: 120,
		},
		final: {
			borderLeftColor: blueGrey[900],
			width: 120,
		},
		decision: {
			borderLeftColor: theme.palette.raip.main,
			width: 68,
		},
	};

	const actorContent = {
		organization: {
			Icon: BusinessRounded,
			label: "Responsável da Organização",
		},
		ethicist: { Icon: AssignmentIndRounded, label: "Eticista" },
		member: { Icon: BadgeRounded, label: "Membro do Comitê de Avaliação" },
		system: { Icon: SettingsRounded, label: "Sistema" },
	};

	return {
		styles: nodeStyles[nodeType],
		actor: actorType ? actorContent[actorType] : null,
	};
}

const FlowNode = (step: StepProps) => {
	const ActorIcon = nodeContent(step).actor?.Icon;

	return (
		<Paper
			sx={{
				position: "relative",
				padding: 2,
				borderLeft: 6,
				borderLeftColor: nodeContent(step).styles.borderLeftColor,
				width: "fit-content",
			}}
		>
			{step.content && (
				<Box sx={{ position: "absolute", top: 3.5, right: 3.5 }}>
					<CustomTooltip
						placement='bottom'
						offsetX={0}
						offsetY={-14}
						title={
							<Box>
								<ul
									style={{
										margin: 0,
										paddingLeft: "1rem",
										paddingTop: "0.2rem",
										display: "flex",
										flexDirection: "column",
										gap: 6,
									}}
								>
									{step.content?.map((item, index) => (
										<li key={index}>
											<Typography
												variant='body2'
												fontSize={"0.8rem"}
												width={180}
											>
												{item.text}
											</Typography>
											{item.items && (
												<ul
													style={{
														margin: 0,
														paddingLeft: "1rem",
														display: "flex",
														flexDirection: "column",
														gap: 2,
													}}
												>
													{item.items.map((subItem, index) => (
														<li key={index}>
															<Typography
																variant='body2'
																fontSize={"0.8rem"}
																width={200}
															>
																{subItem}
															</Typography>
														</li>
													))}
												</ul>
											)}
										</li>
									))}
								</ul>
							</Box>
						}
					>
						<ChatRounded
							fontSize='small'
							sx={{
								color: blueGrey[800],
								width: 15,
								height: 15,
								transform: "scaleX(-1)",
							}}
						/>
					</CustomTooltip>
				</Box>
			)}

			<Typography
				variant='body2'
				textAlign={"center"}
				fontSize={"0.8rem"}
				width={nodeContent(step).styles.width}
			>
				{step.label}
			</Typography>

			{ActorIcon && (
				<Box sx={{ position: "absolute", bottom: 0, left: 6 }}>
					<CustomTooltip
						placement='bottom-end'
						offsetX={0}
						offsetY={-14}
						title={
							<Box sx={{ paddingInline: 2, paddingTop: 0.5, paddingBottom: 1 }}>
								<Typography variant='caption' sx={{ opacity: 0.7 }}>
									Ator
								</Typography>
								<Typography variant='body2'>
									{nodeContent(step).actor?.label}
								</Typography>
							</Box>
						}
					>
						<ActorIcon
							fontSize='small'
							sx={{
								color: blueGrey[600],
								width: 16,
								height: 16,
							}}
						/>
					</CustomTooltip>
				</Box>
			)}
		</Paper>
	);
};

const FlowSubtitles = () => {
	return (
		<CustomTooltip
			placement='bottom'
			offsetX={0}
			offsetY={-14}
			title={
				<Box
					sx={{
						position: "relative",
						paddingLeft: 14,
						paddingRight: 8,
						paddingBlock: 6,
					}}
				>
					<Box
						sx={{
							position: "absolute",
							display: "flex",
							alignItems: "center",
							gap: 1,
							top: "50%",
							left: 14,
							transform: "translateY(-50%)",
						}}
					>
						<Stack spacing={0.4}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
								<Box
									sx={{
										width: 14,
										height: 14,
										borderRadius: "50%",
										backgroundColor: blueGrey[800],
									}}
								/>

								<Typography
									variant='body2'
									fontSize={"0.7rem"}
									color={blueGrey[800]}
								>
									Início/Fim
								</Typography>
							</Box>
							<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
								<Box
									sx={{
										width: 14,
										height: 14,
										borderRadius: "50%",
										backgroundColor: orange[600],
									}}
								/>

								<Typography
									variant='body2'
									fontSize={"0.7rem"}
									color={blueGrey[800]}
								>
									Processo
								</Typography>
							</Box>
							<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
								<Box
									sx={{
										width: 14,
										height: 14,
										borderRadius: "50%",
										backgroundColor: theme.palette.raip.main,
									}}
								/>

								<Typography
									variant='body2'
									fontSize={"0.7rem"}
									color={blueGrey[800]}
								>
									Decisão
								</Typography>
							</Box>
						</Stack>

						<WestRounded sx={{ width: 16, height: 16, color: blueGrey[800] }} />
					</Box>

					<Paper
						sx={{
							position: "relative",
							padding: 1,
							paddingInline: 4,
							borderLeft: 6,
							borderLeftColor: blueGrey[300],
							width: "fit-content",
						}}
					>
						<Box
							sx={{
								position: "absolute",
								bottom: -38,
								left: 6,
								display: "flex",
								flexDirection: "column",
								gap: 0.5,
							}}
						>
							<Box
								sx={{
									width: 14,
									height: 14,
									borderRadius: "50%",
									backgroundColor: blueGrey[300],
								}}
							/>

							<SouthRounded
								sx={{ width: 16, height: 16, color: blueGrey[800] }}
							/>

							<Typography
								variant='body2'
								fontSize={"0.8rem"}
								color={blueGrey[800]}
							>
								Tipo de ator
							</Typography>
						</Box>

						<Typography variant='body2' width={80}>
							Nome etapa
						</Typography>

						<Box
							sx={{
								position: "absolute",
								display: "flex",
								gap: 0.5,
								flexDirection: "column",
								alignItems: "center",
								bottom: 18,
								right: -16,
							}}
						>
							<Typography
								variant='body2'
								fontSize={"0.8rem"}
								color={blueGrey[800]}
							>
								Descrição
							</Typography>

							<NorthRounded
								sx={{
									width: 16,
									height: 16,
									color: blueGrey[800],
								}}
							/>

							<Box
								sx={{
									width: 14,
									height: 14,
									borderRadius: "50%",
									backgroundColor: blueGrey[300],
								}}
							/>
						</Box>
					</Paper>
				</Box>
			}
		>
			<Button size='small'>Legenda</Button>
		</CustomTooltip>
	);
};

const FlowDiagram = () => {
	return (
		<Box
			sx={{
				position: "relative",
				display: "flex",
				gap: 1,
				flexDirection: "column",
				padding: 2,
				paddingBlock: 3,
				borderRadius: "0.5rem",
				border: 1,
				borderColor: blueGrey[100],
				justifyContent: "center",
				alignItems: "center",
			}}
		>
			<Box sx={{ position: "absolute", top: 12, left: 12 }}>
				<FlowSubtitles />
			</Box>

			<Stack
				sx={{
					display: "flex",
					gap: 1,
					flexDirection: "column",
					alignItems: "center",
				}}
				divider={<SouthRounded sx={{ color: blueGrey[900] }} />}
			>
				{STEPS.slice(0, -3).map((step, index) => (
					<FlowNode key={index} {...step} />
				))}
			</Stack>

			<Box sx={{ display: "flex", gap: 4, transform: "rotate(180deg)" }}>
				<TurnSharpLeftRounded />
				<TurnSharpRightRounded />
			</Box>

			<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
				<Box
					sx={{
						position: "relative",
						display: "flex",
						alignItems: "center",
						gap: 3,
					}}
				>
					<Box sx={{ position: "absolute", bottom: 22, left: -70 }}>
						<Box
							sx={{
								position: "relative",
								borderRadius: "0.5rem",
								height: 212,
								width: 100,
								borderBottom: `2px dashed ${blueGrey[900]}`,
								borderLeft: `2px dashed ${blueGrey[900]}`,
								borderTop: `2px dashed ${blueGrey[900]}`,
							}}
						>
							<ArrowForwardRounded
								sx={{
									position: "absolute",
									top: -13,
									right: -4,
									bgcolor: "white",
									color: blueGrey[900],
								}}
							/>
						</Box>
					</Box>
					{STEPS.slice(-3, -1).map((step, index) => (
						<FlowNode key={index} {...step} />
					))}
				</Box>

				<SouthRounded
					sx={{
						color: blueGrey[900],
						placeSelf: "flex-end",
						marginRight: 4.5,
					}}
				/>

				<Box sx={{ placeSelf: "flex-end", marginRight: -4 }}>
					{STEPS.slice(-1).map((step, index) => (
						<FlowNode key={index} {...step} />
					))}
				</Box>
			</Box>
		</Box>
	);
};

export function ApplicationFlow() {
	return (
		<>
			<Box>
				<Typography variant='body2' gutterBottom>
					O fluxo principal* da aplicação de uma organização na plataforma
					representam as ações essenciais conforme a metodologia RAIES implica
					na avaliação das aplicações.
				</Typography>

				<Typography variant='body2' fontSize={"0.75rem"} sx={{ opacity: 0.9 }}>
					* Funcionalidades diversas tais como cancelamento, edição e exclusão
					foram omitidas
				</Typography>
			</Box>

			<Typography variant='body2' gutterBottom>
				O sequenciamento de etapas do fluxo de aplicações na plataforma RAIES é
				composto conforme determinado abaixo:
			</Typography>

			<Alert severity='info'>
				<Typography variant='body2' color='inherit'>
					Passe o ponteiro do mouse sobre os ícones das etapas para ler as
					descrições de forma detalhada
				</Typography>
			</Alert>

			<FlowDiagram />
		</>
	);
}
